import React, { useState } from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';

import { setTermsErrors, trackTermsOfUse } from 'actions';
import { Accept } from 'components/Accept/Accept';
import { WithFlysiteUrls } from 'components/HOC/WithFlysiteUrls/WithFlysiteUrls';
import type { FlysiteUrls } from 'hooks/useFlysiteUrls';
import type { RootState } from 'reducers/types';
import { getCurrentLocale, hasTermsError, isOfferAstropay } from 'selectors';
import { createMarkup } from 'utils/createMarkup/createMarkup';
import { useTranslations } from 'utils/translations/useTranslations';

type TermsAndConditionsProps = ConnectedProps<typeof connector> &
  FlysiteUrls & {
    defaultChecked?: boolean;
    isRecurring?: boolean;

    onChange?: (termsValue: boolean) => void;
  };

function TermsAndConditionsComponent({
  defaultChecked,
  hasTermsError,
  isOfferAstropay,
  isRecurring = false,
  onChange = () => {},
  privacyUrl,
  recurringTermsUrl,
  setTermsErrors,
  termsUrl,
  trackTermsOfUse,
}: TermsAndConditionsProps) {
  const i18n = useTranslations();
  const checked = defaultChecked || false;
  const [areTermsAccepted, setAreTermsAccepted] = useState(checked);

  const handleTermsChange = () =>
    areTermsAccepted ? rejectTerms() : acceptTerms();

  const acceptTerms = () => triggerTerms();

  const rejectTerms = () => {
    triggerTerms();
    setTermsErrors();
  };

  const triggerTerms = () => {
    setAreTermsAccepted(!areTermsAccepted);
    onChange(!areTermsAccepted);
  };

  const termsOfUse = (
    <a
      className="Link"
      href={termsUrl}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="termsOfUseLink"
      onClick={() => trackTermsOfUse('terms of use')}
    >
      {i18n.t('termsAndConditions.terms_of_use')}
    </a>
  );

  const recurringTermsOfUse = (
    <a
      className="Link"
      href={recurringTermsUrl}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="recurringTermsOfUseLink"
      onClick={() => trackTermsOfUse('recurring terms of use')}
    >
      {i18n.t('termsAndConditions.recurring_terms_of_use')}
    </a>
  );

  const privacyPolicy = (
    <a
      className="Link"
      href={privacyUrl}
      target="_blank"
      data-testid="privacyPolicyLink"
      rel="noopener noreferrer"
      onClick={() => trackTermsOfUse('privacy policy')}
    >
      {i18n.t('termsAndConditions.privacy_policy')}
    </a>
  );

  const astropayTC = (
    <span
      data-testid="astropay-terms"
      dangerouslySetInnerHTML={createMarkup(
        i18n.t('termsAndConditions.partners.astropay'),
      )}
    />
  );

  const recurringAppendix = (
    <>
      {i18n.t('termsAndConditions.comma_and')} {recurringTermsOfUse}
    </>
  );

  return (
    <Accept
      checked={areTermsAccepted}
      error={hasTermsError && i18n.t('termsAndConditions.error')}
      name="terms-conditions"
      onChange={handleTermsChange}
      required
    >
      {i18n.t('termsAndConditions.terms_read')} {termsOfUse}{' '}
      {i18n.t('termsAndConditions.and')} {privacyPolicy}
      {isRecurring && recurringAppendix}
      {isOfferAstropay && astropayTC}
    </Accept>
  );
}

const mapStateToProps = (state: RootState) => ({
  hasTermsError: hasTermsError(state),
  isOfferAstropay: isOfferAstropay(state),
  locale: getCurrentLocale(state),
});

const mapDispatchToProps = {
  setTermsErrors,
  trackTermsOfUse,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
const TermsAndConditions = connector(
  WithFlysiteUrls(TermsAndConditionsComponent),
);

export { TermsAndConditions };
